/**
 * The PENDING waiting screen (ARCHITECTURE §9.2/§9.3): the device shows its pairing code and waits
 * for an admin to approve it. Polls the session; when the device flips to APPROVED it continues to
 * the role view with no re-login. Receives zero mission data while pending (INT-05b).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { getSession, logout, type Session } from './api.js';

export function PendingScreen({ session, onApproved }: { session: Session; onApproved: () => void }): ReactNode {
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // the pairing code is only returned at login; the pending WS also carries it. For the waiting
    // screen we read it from the login handoff if present, else show the device is awaiting approval.
    const stored = sessionStorage.getItem('lw.pairingCode');
    if (stored) setCode(stored);
    const tick = async (): Promise<void> => {
      const s = await getSession();
      if (!alive) return;
      if (s && !s.pending) onApproved();
    };
    const id = window.setInterval(() => void tick(), 3000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [onApproved]);

  const roleName = { UAV: 'UAV', FDC: 'FDC', OBSERVER: 'Observer', ADMIN: 'Admin' }[session.role];

  return (
    <div className="auth-screen">
      <div className="auth-card pending">
        <div className="pending-spinner" aria-hidden />
        <h1 className="auth-title">Waiting for approval</h1>
        <p className="auth-note">
          Signed in as <strong>{session.user}</strong> ({roleName}). An admin must approve this device before you can join a mission.
        </p>
        {code ? (
          <div className="pairing" data-testid="pairing-code">
            <span className="pairing-label">Read this code to the admin</span>
            <span className="pairing-code">{code.slice(0, 3)}-{code.slice(3)}</span>
          </div>
        ) : (
          <p className="auth-note">Your device is awaiting approval.</p>
        )}
        <button className="auth-link" onClick={() => void logout().then(() => location.reload())}>Sign out</button>
      </div>
    </div>
  );
}
